import type { Parceiro, Cupom } from '@pgcustom/shared-types';
import { ApiError } from '../../core/api-client.js';
import type { Resultado } from '../../core/resultado.js';
import { FORM_FIELD_CSS } from '../../core/form-field-styles.js';
import type { ParceirosApi } from './parceiros.api.js';
import type { PermissoesParceiros } from './parceiros.permissoes.js';

type Modo = 'criar' | 'editar';

/**
 * `<erp-parceiro-form-modal>` — criação/edição de parceiro (cadastro
 * rico, Sprint 7) + gestão de cupons do parceiro (só em modo editar,
 * cupom sempre precisa de um parceiro já existente — `PRODUCT.md`,
 * 3.2). Mesmo padrão de `erp-cliente-form-modal`: um único componente
 * para os dois modos, dependências injetadas via propriedade JS.
 * Quando `permissoes.atualizar` é falso, abre em modo somente-leitura.
 */
export class ErpParceiroFormModal extends HTMLElement {
  private readonly root: ShadowRoot;
  private _parceirosApi: ParceirosApi | null = null;
  private _permissoes: PermissoesParceiros = { ler: false, criar: false, atualizar: false };
  private _aoSalvar: ((parceiro: Parceiro) => void) | null = null;

  private aberto = false;
  private modo: Modo = 'criar';
  private parceiroAtual: Parceiro | null = null;
  private enviando = false;
  private mensagemErro = '';

  private resultadoCupons: Resultado<Cupom[]> = { status: 'carregando' };
  private novoCupomCodigo = '';
  private novoCupomDesconto = '';
  private enviandoCupom = false;
  private erroCupom = '';

  constructor() {
    super();
    this.root = this.attachShadow({ mode: 'open' });
  }

  set parceirosApi(valor: ParceirosApi) {
    this._parceirosApi = valor;
  }

  set permissoes(valor: PermissoesParceiros) {
    this._permissoes = valor;
  }

  set aoSalvar(valor: (parceiro: Parceiro) => void) {
    this._aoSalvar = valor;
  }

  connectedCallback(): void {
    this.render();
  }

  abrirParaCriar(): void {
    this.modo = 'criar';
    this.parceiroAtual = null;
    this.mensagemErro = '';
    this.aberto = true;
    this.render();
  }

  abrirParaEditar(parceiro: Parceiro): void {
    this.modo = 'editar';
    this.parceiroAtual = parceiro;
    this.mensagemErro = '';
    this.novoCupomCodigo = '';
    this.novoCupomDesconto = '';
    this.erroCupom = '';
    this.aberto = true;
    this.resultadoCupons = { status: 'carregando' };
    this.render();
    void this.carregarCupons(parceiro.id);
  }

  private async carregarCupons(parceiroId: string): Promise<void> {
    if (!this._parceirosApi) return;
    this.resultadoCupons = await this._parceirosApi.listarCupons(parceiroId);
    this.render();
  }

  private fechar(): void {
    if (this.enviando) return;
    this.aberto = false;
    this.render();
  }

  private async aoSubmeter(evento: Event): Promise<void> {
    evento.preventDefault();
    if (!this._parceirosApi || this.enviando || this.somenteLeitura()) return;

    const nome = this.valorCampo('#nome').trim();
    const email = this.valorCampo('#email').trim();
    const telefone = this.valorCampo('#telefone').trim();
    const documento = this.valorCampo('#documento').trim();
    const chavePix = this.valorCampo('#chave-pix').trim();
    const ativo = (this.root.querySelector('#ativo') as HTMLInputElement | null)?.checked ?? true;

    if (!nome) {
      this.mensagemErro = 'nome é obrigatório';
      this.render();
      return;
    }

    this.enviando = true;
    this.mensagemErro = '';
    this.render();

    try {
      let parceiro: Parceiro;
      if (this.modo === 'criar') {
        parceiro = await this._parceirosApi.criar({
          nome,
          ...(email ? { email } : {}),
          ...(telefone ? { telefone } : {}),
          ...(documento ? { documento } : {}),
          ...(chavePix ? { chavePix } : {}),
        });
      } else {
        if (!this.parceiroAtual) throw new Error('parceiro ausente para edição');
        parceiro = await this._parceirosApi.atualizar(this.parceiroAtual.id, {
          nome,
          ativo,
          email: email || null,
          telefone: telefone || null,
          documento: documento || null,
          chavePix: chavePix || null,
        });
      }
      this.aberto = false;
      this._aoSalvar?.(parceiro);
    } catch (erro) {
      this.mensagemErro = erro instanceof ApiError ? erro.message : 'não foi possível salvar. tente novamente.';
    } finally {
      this.enviando = false;
      this.render();
    }
  }

  private async aoSubmeterCupom(evento: Event): Promise<void> {
    evento.preventDefault();
    if (!this._parceirosApi || !this.parceiroAtual || this.enviandoCupom) return;

    const codigo = this.novoCupomCodigo.trim();
    const descontoPercentual = Number(this.novoCupomDesconto);

    if (!codigo) {
      this.erroCupom = 'código é obrigatório';
      this.render();
      return;
    }
    if (!Number.isInteger(descontoPercentual) || descontoPercentual < 0 || descontoPercentual > 100) {
      this.erroCupom = 'desconto deve ser um número inteiro entre 0 e 100';
      this.render();
      return;
    }

    this.enviandoCupom = true;
    this.erroCupom = '';
    this.render();

    try {
      await this._parceirosApi.criarCupom(this.parceiroAtual.id, { codigo, descontoPercentual });
      this.novoCupomCodigo = '';
      this.novoCupomDesconto = '';
      await this.carregarCupons(this.parceiroAtual.id);
    } catch (erro) {
      this.erroCupom = erro instanceof ApiError ? erro.message : 'não foi possível criar o cupom. tente novamente.';
      this.render();
    } finally {
      this.enviandoCupom = false;
    }
  }

  private somenteLeitura(): boolean {
    return this.modo === 'editar' && !this._permissoes.atualizar;
  }

  private valorCampo(seletor: string): string {
    return this.root.querySelector<HTMLInputElement>(seletor)?.value ?? '';
  }

  private render(): void {
    const modo = this.modo;
    const parceiro = this.parceiroAtual;
    const somenteLeitura = this.somenteLeitura();
    const titulo = modo === 'criar' ? 'Novo parceiro' : somenteLeitura ? 'Parceiro' : 'Editar parceiro';

    this.root.innerHTML = `
      <style>
        :host { display: block; }
        form { display: flex; flex-direction: column; gap: var(--pg-space-4, 16px); font-family: var(--pg-font-familia-base, system-ui); }
        ${FORM_FIELD_CSS}
        .linha-ativo { flex-direction: row; align-items: center; gap: var(--pg-space-3, 12px); }
        .linha-ativo input { width: auto; padding: 0; }
        .rodape { display: flex; justify-content: flex-end; gap: var(--pg-space-3, 12px); }
        .cupons { margin-top: var(--pg-space-2, 8px); border-top: var(--pg-border-largura, 1px) solid var(--pg-color-borda, #2a2f36); padding-top: var(--pg-space-4, 16px); }
        .cupons h3 { font-size: var(--pg-font-size-sm, 0.875rem); color: var(--pg-color-texto-primario, #f2f3f5); margin: 0 0 var(--pg-space-3, 12px); }
        .lista-cupons { display: flex; flex-direction: column; gap: var(--pg-space-2, 8px); margin-bottom: var(--pg-space-3, 12px); }
        .cupom-item { display: flex; justify-content: space-between; font-size: var(--pg-font-size-sm, 0.875rem); color: var(--pg-color-texto-primario, #f2f3f5); }
        .cupom-form { display: flex; gap: var(--pg-space-2, 8px); align-items: flex-end; }
        .cupom-form label { flex: 1; }
        .vazio { font-size: var(--pg-font-size-xs, 0.75rem); color: var(--pg-color-texto-desabilitado, #5c626b); }
      </style>
      <pg-modal id="modal" ${this.aberto ? 'open' : ''}>
        <span slot="header">${escaparHtml(titulo)}</span>
        <form novalidate>
          <label>
            Nome
            <input id="nome" type="text" required maxlength="255" value="${escaparHtml(parceiro?.nome ?? '')}" ${somenteLeitura ? 'disabled' : ''} />
          </label>
          <label>
            E-mail
            <input id="email" type="email" maxlength="255" value="${escaparHtml(parceiro?.email ?? '')}" ${somenteLeitura ? 'disabled' : ''} />
          </label>
          <label>
            Telefone
            <input id="telefone" type="text" maxlength="30" value="${escaparHtml(parceiro?.telefone ?? '')}" ${somenteLeitura ? 'disabled' : ''} />
          </label>
          <label>
            Documento (CPF/CNPJ)
            <input id="documento" type="text" maxlength="30" value="${escaparHtml(parceiro?.documento ?? '')}" ${somenteLeitura ? 'disabled' : ''} />
          </label>
          <label>
            Chave PIX
            <input id="chave-pix" type="text" maxlength="255" value="${escaparHtml(parceiro?.chavePix ?? '')}" ${somenteLeitura ? 'disabled' : ''} />
          </label>
          ${
            modo === 'editar'
              ? `<label class="linha-ativo"><input id="ativo" type="checkbox" ${parceiro?.ativo ? 'checked' : ''} ${somenteLeitura ? 'disabled' : ''} /> Ativo</label>`
              : ''
          }
          <span class="erro">${escaparHtml(this.mensagemErro)}</span>
        </form>
        ${modo === 'editar' ? this.renderCupons() : ''}
        <div slot="footer" class="rodape">
          <pg-button id="botao-cancelar" variant="ghost">${somenteLeitura ? 'Fechar' : 'Cancelar'}</pg-button>
          ${!somenteLeitura ? `<pg-button id="botao-salvar" variant="primary" ${this.enviando ? 'loading' : ''}>Salvar</pg-button>` : ''}
        </div>
      </pg-modal>
    `;

    const form = this.root.querySelector('form');
    form?.addEventListener('submit', (evento) => this.aoSubmeter(evento));
    this.root.querySelector('#botao-salvar')?.addEventListener('pg-click', (evento) => this.aoSubmeter(evento));
    this.root.querySelector('#botao-cancelar')?.addEventListener('pg-click', () => this.fechar());
    this.root.querySelector('#modal')?.addEventListener('pg-close', () => this.fechar());

    const cupomForm = this.root.querySelector('#form-cupom');
    cupomForm?.addEventListener('submit', (evento) => this.aoSubmeterCupom(evento));
    this.root.querySelector('#botao-add-cupom')?.addEventListener('pg-click', (evento) => this.aoSubmeterCupom(evento));
    this.root.querySelector('#novo-cupom-codigo')?.addEventListener('input', (evento) => {
      this.novoCupomCodigo = (evento.target as HTMLInputElement).value;
    });
    this.root.querySelector('#novo-cupom-desconto')?.addEventListener('input', (evento) => {
      this.novoCupomDesconto = (evento.target as HTMLInputElement).value;
    });
  }

  private renderCupons(): string {
    const somenteLeitura = this.somenteLeitura();
    let listaHtml: string;
    if (this.resultadoCupons.status === 'ok') {
      listaHtml = this.resultadoCupons.dado
        .map(
          (cupom) =>
            `<div class="cupom-item"><span>${escaparHtml(cupom.codigo)}</span><span>${cupom.descontoPercentual}% ${cupom.ativo ? '' : '(inativo)'}</span></div>`
        )
        .join('');
    } else if (this.resultadoCupons.status === 'carregando') {
      listaHtml = '<span class="vazio">carregando…</span>';
    } else if (this.resultadoCupons.status === 'vazio') {
      listaHtml = '<span class="vazio">nenhum cupom cadastrado ainda</span>';
    } else {
      listaHtml = '<span class="vazio">não foi possível carregar os cupons</span>';
    }

    return `
      <div class="cupons">
        <h3>Cupons</h3>
        <div class="lista-cupons">${listaHtml}</div>
        ${
          somenteLeitura
            ? ''
            : `<form id="form-cupom" class="cupom-form" novalidate>
                 <label>Código <input id="novo-cupom-codigo" type="text" maxlength="50" value="${escaparHtml(this.novoCupomCodigo)}" /></label>
                 <label>Desconto % <input id="novo-cupom-desconto" type="number" min="0" max="100" value="${escaparHtml(this.novoCupomDesconto)}" /></label>
                 <pg-button id="botao-add-cupom" variant="secondary" ${this.enviandoCupom ? 'loading' : ''}>Adicionar</pg-button>
               </form>
               <span class="erro">${escaparHtml(this.erroCupom)}</span>`
        }
      </div>
    `;
  }
}

function escaparHtml(valor: string): string {
  const div = document.createElement('div');
  div.textContent = valor;
  return div.innerHTML;
}

customElements.define('erp-parceiro-form-modal', ErpParceiroFormModal);

declare global {
  interface HTMLElementTagNameMap {
    'erp-parceiro-form-modal': ErpParceiroFormModal;
  }
}
